/**
 * ChatPage — `/chat`
 *
 * Dedicated full-page chat experience. Left pane = team members (with live
 * presence dots) + recent DMs + workspace rooms. Right pane = the active
 * conversation, powered by the existing WorkspaceChat component which
 * already handles rooms, @mentions, typing indicators, reactions, and
 * note attachments.
 *
 * "Start a DM" is a single click on any teammate — backend auto-creates
 * a hidden 1:1 room on first message.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import WorkspaceChat from '@/components/WorkspaceChat';
import { MessageCircle, Hash, Search, Circle, Users, Bell, BellOff } from 'lucide-react';
import { toast } from 'sonner';
import usePushNotifications from '@/hooks/usePushNotifications';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_META = {
  active:  { color: '#34C759', label: 'Online',  dot: 'bg-[#34C759]' },
  idle:    { color: '#FFB340', label: 'Away',    dot: 'bg-[#FFB340]' },
  busy:    { color: '#FF3B30', label: 'Busy',    dot: 'bg-[#FF3B30]' },
  offline: { color: '#9CA3AF', label: 'Offline', dot: 'bg-[#9CA3AF]' },
};

const StatusDot = ({ status }) => (
  <span className={`w-2.5 h-2.5 rounded-full ${STATUS_META[status]?.dot || STATUS_META.offline.dot} inline-block flex-shrink-0 ring-2 ring-white`} />
);

const ChatPage = () => {
  const { token, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const push = usePushNotifications(token);

  const handleTogglePush = async () => {
    if (push.isSubscribed) {
      await push.unsubscribe();
      toast.success('Notifications turned off for this browser');
    } else {
      const r = await push.subscribe();
      if (r?.ok) toast.success('Notifications enabled — try closing the tab and sending a DM!');
      else if (r?.error) toast.error(r.error);
    }
  };

  const [presence, setPresence] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [unread, setUnread] = useState({ total: 0, by_room: {} });
  const [mutedRooms, setMutedRooms] = useState([]);
  const [activeRoomId, setActiveRoomId] = useState(searchParams.get('room') || 'workspace');
  const [activeDMPartner, setActiveDMPartner] = useState(null);
  const [query, setQuery] = useState('');

  const fetchPresence = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/presence`, { headers });
      setPresence(r.data);
    } catch {}
  }, [headers]);

  const fetchRooms = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/workspace/rooms`, { headers });
      setRooms((r.data.rooms || []).filter(rm => rm.type !== 'dm'));
    } catch {}
  }, [headers]);

  const fetchUnread = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/chat/unread`, { headers });
      setUnread(r.data);
    } catch {}
  }, [headers]);

  const fetchMutes = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/settings/notifications`, { headers });
      setMutedRooms(r.data.muted_rooms || []);
    } catch {}
  }, [headers]);

  const toggleMute = async (roomId, e) => {
    e?.stopPropagation();
    const currentlyMuted = mutedRooms.includes(roomId);
    try {
      if (currentlyMuted) {
        await axios.delete(`${API}/settings/notifications/mute-room/${encodeURIComponent(roomId)}`, { headers });
        setMutedRooms(prev => prev.filter(r => r !== roomId));
        toast.success('Room unmuted');
      } else {
        await axios.post(`${API}/settings/notifications/mute-room/${encodeURIComponent(roomId)}`, {}, { headers });
        setMutedRooms(prev => [...prev, roomId]);
        toast.success('Room muted — no pushes from this room');
      }
    } catch {
      toast.error('Could not toggle mute');
    }
  };

  const markRead = useCallback(async (roomId) => {
    try {
      await axios.post(`${API}/chat/rooms/${roomId}/read`, {}, { headers });
      fetchUnread();
    } catch {}
  }, [headers, fetchUnread]);

  const startDM = async (targetUser) => {
    try {
      const r = await axios.post(`${API}/chat/dm/start`, { target_user_id: targetUser.user_id }, { headers });
      setActiveRoomId(r.data.room_id);
      setActiveDMPartner(r.data.other_user);
      setSearchParams({ room: r.data.room_id });
      markRead(r.data.room_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start DM');
    }
  };

  const selectRoom = (roomId, partner = null) => {
    setActiveRoomId(roomId);
    setActiveDMPartner(partner);
    setSearchParams(roomId === 'workspace' ? {} : { room: roomId });
    markRead(roomId);
  };

  useEffect(() => {
    fetchPresence();
    fetchRooms();
    fetchUnread();
    fetchMutes();
    const t = setInterval(() => { fetchPresence(); fetchUnread(); }, 30000);
    return () => clearInterval(t);
  }, [fetchPresence, fetchRooms, fetchUnread, fetchMutes]);

  // Mark the initial active room as read on page open
  useEffect(() => {
    if (activeRoomId) markRead(activeRoomId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Heartbeat so current user shows as active while on this page
  useEffect(() => {
    if (!token) return;
    const hb = () => axios.post(`${API}/presence/heartbeat`, { status: 'active' }, { headers }).catch(() => {});
    hb();
    const t = setInterval(hb, 60000);
    return () => clearInterval(t);
  }, [token, headers]);

  // Honor ?dm=<email> deep-link from Dashboard widget
  useEffect(() => {
    const dmEmail = searchParams.get('dm');
    if (dmEmail && presence?.users) {
      const target = presence.users.find(u => u.email === dmEmail);
      if (target && target.user_id !== user?.user_id) {
        startDM(target);
        // Clean URL
        const next = new URLSearchParams(searchParams);
        next.delete('dm');
        setSearchParams(next);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presence]);

  const filteredUsers = (presence?.users || [])
    .filter(u => u.user_id !== user?.user_id)
    .filter(u => !query || (u.name || '').toLowerCase().includes(query.toLowerCase()) || (u.email || '').toLowerCase().includes(query.toLowerCase()));

  // Given a teammate user_id, compute the deterministic DM room_id (must match backend _dm_room_id)
  const dmRoomIdFor = (otherId) => {
    if (!user?.user_id) return null;
    const [lo, hi] = [user.user_id, otherId].sort();
    return `dm_${lo}_${hi}`;
  };

  const onlineCount = presence?.online || 0;
  const currentRoomLabel =
    activeDMPartner ? `DM with ${activeDMPartner.name || activeDMPartner.email}`
    : activeRoomId === 'workspace' ? 'Workspace chat'
    : rooms.find(r => r.room_id === activeRoomId)?.name || 'Chat';

  return (
    <div className="flex h-[calc(100vh-60px)] bg-white" data-testid="chat-page">
      {/* LEFT PANE — users & rooms */}
      <aside className="w-[300px] border-r border-[#E5E7EB] flex flex-col">
        <div className="p-3 border-b border-[#E5E7EB]">
          <h1 className="text-lg font-bold text-[#0A0A0A] flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-[#002FA7]" />
            Chat
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge className="bg-[#34C759]/15 text-[#34C759] text-[10px]">
              <Circle className="w-2 h-2 mr-1 fill-current" />{onlineCount} online
            </Badge>
            <span className="text-[10px] text-[#9CA3AF]">{presence?.total || 0} members</span>
            {push.isSupported && push.permission !== 'denied' && (
              <Button
                onClick={handleTogglePush}
                disabled={push.busy}
                size="sm"
                variant="ghost"
                className="h-5 px-1.5 text-[10px] ml-auto"
                title={push.isSubscribed ? 'Disable browser notifications' : 'Enable browser notifications'}
                data-testid="push-toggle-btn"
              >
                {push.isSubscribed ? <Bell className="w-3 h-3 text-[#34C759]" /> : <BellOff className="w-3 h-3 text-[#9CA3AF]" />}
              </Button>
            )}
          </div>
          <div className="mt-2 relative">
            <Search className="absolute left-2 top-2 w-3.5 h-3.5 text-[#9CA3AF]" />
            <Input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search people…"
              className="pl-7 h-8 text-xs"
              data-testid="chat-search-input"
            />
          </div>
        </div>

        <ScrollArea className="flex-1">
          {/* Workspace + rooms */}
          <div className="p-2">
            <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF] px-2 py-1">Channels</p>
            <button
              onClick={() => selectRoom('workspace')}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-xs hover:bg-[#F3F4F6] group ${activeRoomId === 'workspace' ? 'bg-[#002FA7]/10 text-[#002FA7]' : 'text-[#111827]'}`}
              data-testid="room-workspace"
            >
              <Hash className="w-3.5 h-3.5" /> <span className="flex-1">Workspace</span>
              {unread.by_room?.workspace > 0 && (
                <Badge className="bg-[#FF3B30] text-white text-[9px] h-4 px-1.5" data-testid="unread-workspace">{unread.by_room.workspace}</Badge>
              )}
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => toggleMute('workspace', e)}
                onKeyDown={(e) => { if (e.key === 'Enter') toggleMute('workspace', e); }}
                className="opacity-60 hover:opacity-100 cursor-pointer p-0.5"
                title={mutedRooms.includes('workspace') ? 'Unmute room' : 'Mute room'}
                data-testid="mute-workspace"
              >
                {mutedRooms.includes('workspace')
                  ? <BellOff className="w-3 h-3 text-[#9CA3AF]" />
                  : <Bell className="w-3 h-3 text-[#9CA3AF] hidden group-hover:inline" />}
              </span>
            </button>
            {rooms.map(r => (
              <button
                key={r.room_id}
                onClick={() => selectRoom(r.room_id)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left text-xs hover:bg-[#F3F4F6] group ${activeRoomId === r.room_id ? 'bg-[#002FA7]/10 text-[#002FA7]' : 'text-[#111827]'}`}
                data-testid={`room-${r.room_id}`}
              >
                <Hash className="w-3.5 h-3.5" /> <span className="truncate flex-1">{r.name}</span>
                {unread.by_room?.[r.room_id] > 0 && (
                  <Badge className="bg-[#FF3B30] text-white text-[9px] h-4 px-1.5" data-testid={`unread-${r.room_id}`}>{unread.by_room[r.room_id]}</Badge>
                )}
                <span
                  role="button"
                  tabIndex={0}
                  onClick={(e) => toggleMute(r.room_id, e)}
                  onKeyDown={(e) => { if (e.key === 'Enter') toggleMute(r.room_id, e); }}
                  className="opacity-60 hover:opacity-100 cursor-pointer p-0.5"
                  title={mutedRooms.includes(r.room_id) ? 'Unmute room' : 'Mute room'}
                  data-testid={`mute-${r.room_id}`}
                >
                  {mutedRooms.includes(r.room_id)
                    ? <BellOff className="w-3 h-3 text-[#9CA3AF]" />
                    : <Bell className="w-3 h-3 text-[#9CA3AF] hidden group-hover:inline" />}
                </span>
              </button>
            ))}
          </div>

          {/* People */}
          <div className="p-2 border-t border-[#F3F4F6]">
            <p className="text-[10px] uppercase tracking-wide text-[#9CA3AF] px-2 py-1 flex items-center justify-between">
              <span>Direct messages</span>
              <Users className="w-3 h-3" />
            </p>
            {filteredUsers.length === 0 && (
              <p className="text-[11px] text-[#9CA3AF] px-2 py-1">No matching users</p>
            )}
            {filteredUsers.map(u => {
              const dmId = dmRoomIdFor(u.user_id);
              const dmUnread = dmId ? (unread.by_room?.[dmId] || 0) : 0;
              const isMuted = dmId && mutedRooms.includes(dmId);
              return (
              <button
                key={u.user_id}
                onClick={() => startDM(u)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left hover:bg-[#F3F4F6] group ${activeDMPartner?.user_id === u.user_id ? 'bg-[#002FA7]/10' : ''}`}
                data-testid={`user-${u.user_id}`}
              >
                <StatusDot status={u.status} />
                <div className="flex-1 min-w-0">
                  <p className={`text-xs font-medium truncate ${activeDMPartner?.user_id === u.user_id ? 'text-[#002FA7]' : 'text-[#111827]'}`}>
                    {u.name || u.email}
                  </p>
                  <p className="text-[9px] text-[#9CA3AF] truncate">
                    {u.role || 'member'} · {STATUS_META[u.status]?.label}
                  </p>
                </div>
                {dmUnread > 0 && (
                  <Badge className="bg-[#FF3B30] text-white text-[9px] h-4 px-1.5" data-testid={`unread-dm-${u.user_id}`}>{dmUnread}</Badge>
                )}
                {dmId && (
                  <span
                    role="button"
                    tabIndex={0}
                    onClick={(e) => toggleMute(dmId, e)}
                    onKeyDown={(e) => { if (e.key === 'Enter') toggleMute(dmId, e); }}
                    className="opacity-60 hover:opacity-100 cursor-pointer p-0.5"
                    title={isMuted ? 'Unmute this DM' : 'Mute this DM'}
                    data-testid={`mute-dm-${u.user_id}`}
                  >
                    {isMuted
                      ? <BellOff className="w-3 h-3 text-[#9CA3AF]" />
                      : <Bell className="w-3 h-3 text-[#9CA3AF] hidden group-hover:inline" />}
                  </span>
                )}
              </button>
              );
            })}
          </div>
        </ScrollArea>
      </aside>

      {/* RIGHT PANE — active conversation */}
      <main className="flex-1 flex flex-col" data-testid="chat-main-pane">
        <div className="border-b border-[#E5E7EB] px-4 py-2 flex items-center gap-2">
          {activeDMPartner ? <MessageCircle className="w-4 h-4 text-[#002FA7]" /> : <Hash className="w-4 h-4 text-[#002FA7]" />}
          <h2 className="text-sm font-semibold text-[#0A0A0A]" data-testid="active-conversation-title">{currentRoomLabel}</h2>
          {activeDMPartner && (
            <Badge className="bg-[#F3F4F6] text-[#4B5563] text-[10px]">1:1</Badge>
          )}
        </div>
        <div className="flex-1 min-h-0">
          {/* We reuse WorkspaceChat — which accepts a `rooms` prop for the room selector.
              Passing the active room_id via a key forces a remount on switch so message
              history + WS connection re-initialize cleanly. */}
          <WorkspaceChat
            key={activeRoomId}
            rooms={rooms}
            hideRoomSelector
            initialRoomId={activeRoomId}
            variant="embedded"
          />
        </div>
      </main>
    </div>
  );
};

export default ChatPage;
